import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { textOverflow, whiteSpace } from '../utils/customPropStyles';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import getQueryResults from '../utils/query/getQueryResults';
import ShareclassColumnValue from './ShareclassColumnValue';
import ShareclassRowExpander from './ShareclassRowExpander';
import Tooltip from './Tooltip';

const ShareclassRow = styled<any, any>('tr')`
    ${propStyles};
`;

ShareclassRow.defaultProps = {
    cursor: 'pointer',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    alternated: {
        bgColor: 'gray100',
    },
    hover: {
        bgColor: 'gray200',
    },
};

export const ShareclassColumn = styled<any, any>('td')`
    ${propStyles};
    ${textOverflow};
    ${whiteSpace};
`;

ShareclassColumn.defaultProps = {
    paddingX: 'sm',
    paddingY: 'md',
    fontSize: 'sm',
    overflow: ' hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

@observer
class ShareclassRows extends Component<any, any> {
    @computed
    get checkEnableByFundList() {
        const { shareClass, rowExpanderConfig, dataStore } = this.props;
        const fundListCheck =
            rowExpanderConfig && rowExpanderConfig.fundListCheck;
        if (fundListCheck) {
            return dataStore.fundListContainsEntity(shareClass, fundListCheck);
        }
        return true;
    }

    @computed
    get isRowExpandDisabled() {
        if (!this.checkEnableByFundList || this.isShareclassDisabled) {
            return true;
        }
        return false;
    }

    @computed
    get isShareclassDisabled() {
        const { shareClass, rowExpanderConfig } = this.props;
        if (rowExpanderConfig && rowExpanderConfig.disabledFlag) {
            const conditionalConfig = rowExpanderConfig.disabledFlag;
            const values = normalizeQueryResults({
                ...shareClass.properties_pub,
                ...getConditionalData(shareClass, conditionalConfig),
            });
            return getConditionalDataFlag(values, conditionalConfig);
        }
        return false;
    }

    @computed
    get disabledTooltipDisclaimer() {
        const { dataStore, rowExpanderConfig, queryContext } = this.props;
        if (rowExpanderConfig.disabledTooltipDisclaimer) {
            const disclaimerQuery = {
                disclaimer: rowExpanderConfig.disabledTooltipDisclaimer,
            };
            const data = getQueryResults(
                queryContext,
                disclaimerQuery,
                dataStore.culture
            );
            if (data && data.disclaimer) {
                return data.disclaimer;
            }
        }
        return;
    }

    renderRowExpander(shareClassProp) {
        const { rowExpanderConfig, theme } = this.props;
        if (!this.isRowExpandDisabled) {
            return (
                <ShareclassRowExpander
                    rowExpanderConfig={rowExpanderConfig}
                    shareClass={shareClassProp}
                />
            );
        } else {
            const disabledTooltip = this.disabledTooltipDisclaimer;
            if (disabledTooltip) {
                return (
                    <ShareclassColumn
                        textAlign="center"
                        {...theme.ShareclassColumn}
                    >
                        <Tooltip content={[disabledTooltip]} />
                    </ShareclassColumn>
                );
            }
        }
        return <ShareclassColumn {...theme.ShareclassColumn} />;
    }

    render() {
        const {
            theme,
            alternated,
            rowHasExpandableOptions,
            rowExpanderConfig,
            selectedGroupMap,
            children,
            dataStore,
            queryContext,
            ...shareClass
        } = this.props;

        const rowExpandable = !this.isRowExpandDisabled;
        const shareclassRowTheme = rowExpandable
            ? theme.ShareclassRow
            : { ...theme.ShareclassRow, ...theme.ShareclassRowDisabled };

        const shareclassColumnTheme = rowExpandable
            ? theme.ShareclassColumn
            : { ...theme.ShareclassColumn, ...theme.ShareclassColumnDisabled };

        return (
            <Fragment>
                <ShareclassRow
                    data-alternated={alternated}
                    data-selected={
                        rowHasExpandableOptions &&
                        shareClass.expanded &&
                        rowExpandable
                    }
                    onClick={
                        rowHasExpandableOptions && rowExpandable
                            ? shareClass.toggleExpanded
                            : () => null
                    }
                    cursor={rowExpandable ? 'pointer' : 'auto'}
                    {...shareclassRowTheme}
                >
                    {rowHasExpandableOptions &&
                        (!rowExpanderConfig ||
                            rowExpanderConfig.align === 'left') &&
                        this.renderRowExpander(shareClass)}
                    {shareClass.columns.map((column, index) => (
                        <ShareclassColumn
                            key={`${column.type}-${index}`}
                            textAlign={column.textAlign || 'center'}
                            width={column.width}
                            overflow={
                                column.type === 'document' || column.wrapText
                                    ? 'visible'
                                    : 'hidden'
                            }
                            whiteSpace={column.wrapText ? 'normal' : 'nowrap'}
                            {...shareclassColumnTheme}
                        >
                            <ShareclassColumnValue
                                shareClass={shareClass}
                                column={column}
                                columnIndex={index}
                                selectedGroupMap={selectedGroupMap}
                                isShareclassDisabled={this.isShareclassDisabled}
                                queryContext={queryContext}
                            />
                        </ShareclassColumn>
                    ))}
                    {rowHasExpandableOptions &&
                        rowExpanderConfig &&
                        rowExpanderConfig.align === 'right' &&
                        this.renderRowExpander(shareClass)}
                </ShareclassRow>
                {rowHasExpandableOptions &&
                    shareClass.expanded &&
                    rowExpandable && (
                        <tr>
                            <td
                                colSpan={
                                    rowHasExpandableOptions
                                        ? shareClass.columns.length + 1
                                        : shareClass.columns.length
                                }
                            >
                                {children}
                            </td>
                        </tr>
                    )}
            </Fragment>
        );
    }
}

export default withTheme(ShareclassRows) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassRows.tsx